import { SharedState } from "@/common/state";

type ReportAction =
  | "generated"
  | "downloaded"
  | "deleted"
  | "retried"
  | "details_inspected"
  | "scheduled"
  | "schedule_updated"
  | "schedule_deleted";

type ReportType = "csv" | "pdf" | "executive" | "scheduled" | "export";

type HumanReadableParam = {
  type: string;
  value: unknown;
};

type ReportTrackingMetadata = {
  report_id?: string;
  report_name: string;
  report_type: ReportType;
  report_subtype?: string;
  entity_type?: string;
  status?: string;
  context?: string;
  source?: string;
  template_id?: string;
  template_name?: string;
  filters?: unknown;
  parameters?: unknown;
  selected_columns?: string[];
  data_sources?: string[];
  schedule?: {
    cadence?: string;
    recipient_count?: number;
  };
};

declare global {
  interface Window {
    woopra?: {
      identify: (payload: Record<string, unknown>) => Window["woopra"];
      update: (payload?: Record<string, unknown>) => void;
      track: (eventName: string, payload?: Record<string, unknown>) => void;
    };
  }
}

const PII_KEY_PATTERN = /recipient|subscriber|mailing[_ -]?list|email|e-mail/i;
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

const compact = (obj: Record<string, unknown>) => {
  const cleaned: Record<string, unknown> = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

const humanizeKey = (key: string) =>
  key
    .replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (s) => s.toUpperCase());

const escapeHtml = (value: unknown) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const sanitizeValue = (value: unknown, key = ""): unknown => {
  if (PII_KEY_PATTERN.test(key)) {
    if (Array.isArray(value)) {
      return `${value.length} recipient${value.length === 1 ? "" : "s"}`;
    }
    return "[redacted]";
  }

  if (typeof value === "string") {
    return value.replace(EMAIL_PATTERN, "[redacted-email]").slice(0, 500);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, key));
  }

  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).reduce(
      (acc, [childKey, childValue]) => {
        acc[childKey] = sanitizeValue(childValue, childKey);
        return acc;
      },
      {} as Record<string, unknown>
    );
  }

  return value;
};

const normalizeParams = (input: unknown): HumanReadableParam[] => {
  if (!input) {
    return [];
  }

  if (Array.isArray(input)) {
    return input
      .map((item) => {
        if (typeof item === "string") {
          const [type, ...valueParts] = item.split(":");
          return {
            type: type || "parameter",
            value: valueParts.join(":") || "selected",
          };
        }
        if (item && typeof item === "object") {
          const obj = item as Record<string, unknown>;
          return {
            type: String(obj.type || obj.key || obj.name || "parameter"),
            value: obj.value || obj.label || obj.id || obj,
          };
        }
        return { type: "parameter", value: item };
      })
      .filter((param) => !PII_KEY_PATTERN.test(param.type));
  }

  if (typeof input === "object") {
    return Object.entries(input as Record<string, unknown>)
      .filter((entry) => {
        const value = entry[1];
        return value !== undefined && value !== null && value !== "";
      })
      .filter(([key]) => !PII_KEY_PATTERN.test(key))
      .map(([key, value]) => ({
        type: key,
        value,
      }));
  }

  return [{ type: "parameter", value: input }];
};

export const reportParametersText = (input: unknown) => {
  const params = normalizeParams(input);
  if (params.length === 0) {
    return "No filters selected";
  }

  return params
    .map(({ type, value }) => {
      const safeValue = sanitizeValue(value, type);
      if (Array.isArray(safeValue)) {
        return `${humanizeKey(type)}: ${safeValue.join(", ")}`;
      }
      if (safeValue && typeof safeValue === "object") {
        return `${humanizeKey(type)}: ${JSON.stringify(safeValue)}`;
      }
      return `${humanizeKey(type)}: ${safeValue}`;
    })
    .join("; ");
};

const createAuditSummary = (
  action: ReportAction,
  metadata: ReportTrackingMetadata,
  parametersText: string
) => {
  const orgName = SharedState.loggedUser.value.orgName || "unknown organization";
  const environment = SharedState.env.value.env || "unknown environment";
  return `${humanizeKey(action)} ${metadata.report_type} report "${
    metadata.report_name
  }" for ${orgName} in ${environment}. Parameters: ${parametersText}`;
};

export const trackReportEvent = (
  action: ReportAction,
  metadata: ReportTrackingMetadata
) => {
  const user = SharedState.loggedUser.value;
  const env = SharedState.env.value;
  const actorEmail = user.email;
  const company = user.orgName;
  const parameters = metadata.parameters || metadata.filters;
  const parametersText = reportParametersText(parameters);
  const payload = compact({
    action,
    email: actorEmail,
    EmailAddress: actorEmail,
    Emailaddress: actorEmail,
    company,
    Company: company,
    report_id: metadata.report_id,
    report_name: sanitizeValue(metadata.report_name, "report_name"),
    report_type: metadata.report_type,
    report_subtype: metadata.report_subtype,
    entity_type: metadata.entity_type,
    status: metadata.status,
    template_id: metadata.template_id,
    template_name: metadata.template_name,
    data_sources: metadata.data_sources,
    selected_columns: metadata.selected_columns,
    parameters: sanitizeValue(parameters),
    parameters_text: parametersText,
    schedule_cadence: metadata.schedule?.cadence,
    recipient_count: metadata.schedule?.recipient_count,
    organization: company,
    org_id: company,
    partner_id: user.partnerId,
    environment: env.env,
    context: metadata.context || location.pathname,
    source: metadata.source,
    audit_summary: createAuditSummary(action, metadata, parametersText),
  });

  try {
    window.woopra?.identify(
      compact({
        email: actorEmail,
        EmailAddress: actorEmail,
        Emailaddress: actorEmail,
        company,
        Company: company,
        organization: company,
        partner_id: user.partnerId,
        environment: env.env,
      })
    );
    window.woopra?.track(`report_${action}`, payload);
    window.woopra?.update();
  } catch (error) {
    /* ignore Woopra client errors */
  }
};

export const reportDetailsHtml = (metadata: ReportTrackingMetadata) => {
  const parameters = reportParametersText(metadata.parameters || metadata.filters);
  return [
    `<b>Name:</b> ${escapeHtml(
      sanitizeValue(metadata.report_name, "report_name")
    )}`,
    `<b>Type:</b> ${escapeHtml(metadata.report_type)}`,
    metadata.report_subtype
      ? `<b>Subtype:</b> ${escapeHtml(metadata.report_subtype)}`
      : "",
    metadata.entity_type ? `<b>Entity:</b> ${escapeHtml(metadata.entity_type)}` : "",
    metadata.status ? `<b>Status:</b> ${escapeHtml(metadata.status)}` : "",
    metadata.data_sources?.length
      ? `<b>Data sources:</b> ${escapeHtml(metadata.data_sources.join(", "))}`
      : "",
    `<b>Parameters:</b> ${escapeHtml(parameters)}`,
  ]
    .filter(Boolean)
    .join("<br/>");
};
